/**
 * Sending a course to Mauvely Review.
 *
 * The copy has one job beyond confirming an action: **saying that this leaves
 * the vault.** Everywhere else in Cloud, "nobody at Mauvely can read this" is
 * literally true. A course sent for review is readable by Review's server,
 * because a reviewer has no account and therefore no key, and a link that only
 * its recipient could decrypt would be a link nobody could open.
 *
 * Publishing already crossed that line and says so in its own dialog. This says
 * it too, in the same place and for the same reason: at the moment of the
 * decision, not in a footnote afterwards.
 */

import { useEffect, useState } from 'react';
import { api } from '../api';
import { ds } from '../ds';
import type { DecryptedNode } from '../nodes';
import { sendToReview, type SendProgress } from '../review';
import { display, mono, RADIUS, SCRIM, SHADOW } from '../theme';
import { useT } from '../i18n';

const PHASE: Record<SendProgress['phase'], string> = {
  downloading: 'Decrypting the course',
  checking: 'Checking the file',
  uploading: 'Sending to Review',
  finishing: 'Finishing up'
};

export function SendToReviewDialog({
  node,
  nodeKey,
  onClose
}: {
  node: DecryptedNode;
  nodeKey: CryptoKey | null;
  onClose: () => void;
}) {
  const t = useT();
  const { Button } = ds();
  const [progress, setProgress] = useState<SendProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ url: string } | null>(null);
  const [existing, setExisting] = useState<{ projectId: string; round: number } | null>(null);
  const [asNewProject, setAsNewProject] = useState(false);

  useEffect(() => {
    api.review
      .forNode(node.id)
      .then((r) => {
        if (r.projectId) setExisting({ projectId: r.projectId, round: r.round ?? 1 });
      })
      .catch(() => undefined);
  }, [node.id]);

  const busy = progress !== null && !done && !error;
  const resending = !!existing && !asNewProject;

  async function run() {
    if (!nodeKey) {
      setError('That file is not unlocked in this tab. Reload and try again.');
      return;
    }
    setError(null);
    try {
      const result = await sendToReview(
        node,
        nodeKey,
        setProgress,
        resending ? existing!.projectId : undefined
      );
      setDone({ url: result.url });
    } catch (err) {
      // The messages thrown in review.ts are already written for a person —
      // "That file isn't a Mauvely course", and the plan cap naming its number
      // and the way out — so they are shown rather than replaced.
      setError(err instanceof Error ? err.message : 'That course could not be sent.');
      setProgress(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('send-to-review-dialog.send-for-review')}
      className="mv-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        background: SCRIM,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        zIndex: 50
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="mv-pop"
        style={{
          width: 'min(520px, 100%)',
          background: 'var(--surface-card)',
          borderRadius: RADIUS.panel,
          boxShadow: SHADOW.xl,
          padding: '30px 32px 26px'
        }}
      >
        <p style={mono}>{t('send-to-review-dialog.send-for-review')}</p>
        <h2 style={{ ...display(26), marginTop: 10 }}>{node.name}</h2>

        {done ? (
          <>
            <p style={{ color: 'var(--text-body)', marginTop: 14, lineHeight: 1.6 }}>
              {t('send-to-review-dialog.the-review-is-ready-open')}
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={onClose}>
                {t('send-to-review-dialog.close')}
              </Button>
              <Button onClick={() => window.open(done.url, '_blank', 'noopener')}>
                {t('send-to-review-dialog.open-in-review')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p style={{ color: 'var(--text-body)', marginTop: 14, lineHeight: 1.6 }}>
              Review hosts a copy of this course that is <strong>{t('send-to-review-dialog.not-encrypted')}</strong>, so
              people without a Mauvely account can open it and comment on it.
            </p>

            {existing && (
              <div style={{ marginTop: 16, display: 'grid', gap: 8 }}>
                {(
                  [
                    [false, `Send again — starts round ${existing.round + 1}`, 'Comments carry over; reviewers see the new version.'],
                    [true, 'Send as a new review', 'A separate project, its own link, no shared history.']
                  ] as const
                ).map(([v, title, desc]) => (
                  <label
                    key={String(v)}
                    style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}
                  >
                    <input
                      type="radio"
                      name="review-destination"
                      checked={asNewProject === v}
                      onChange={() => setAsNewProject(v)}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <span style={{ display: 'block', color: 'var(--text-strong)', fontSize: 14 }}>
                        {title}
                      </span>
                      <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
                        {desc}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            {error && (
              <p
                style={{
                  marginTop: 16,
                  padding: '10px 12px',
                  borderRadius: RADIUS.control,
                  background: 'var(--danger-soft, rgba(217,79,111,.12))',
                  color: 'var(--danger-500)',
                  lineHeight: 1.5
                }}
              >
                {error}
              </p>
            )}

            {progress && !error && (
              <div style={{ marginTop: 18 }}>
                <p style={mono}>{PHASE[progress.phase]}</p>
                <div
                  style={{
                    height: 6,
                    marginTop: 8,
                    borderRadius: 999,
                    background: 'var(--surface-sunken)',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${progress.percent}%`,
                      background: 'var(--signal)',
                      transition: 'width 180ms cubic-bezier(.22,.61,.36,1)'
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={onClose} disabled={busy}>
                {t('send-to-review-dialog.cancel')}
              </Button>
              <Button onClick={() => void run()} disabled={busy}>
                {busy ? 'Sending…' : resending ? `Send round ${existing!.round + 1}` : 'Send for review'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
